import React, { useState, useEffect, useMemo } from 'react';
import { useStoreState } from 'easy-peasy';
import * as R from 'ramda';
import classNames from 'classnames';
import FNumber from 'Components/FNumber';
import { Price } from '~/models/utils';
import OwnerButtons from './OwnerButtons';
import PrepopulateButtons from './PrepopulateButtons';
import Tabs from './Tabs';
import Partner from './Partner';
import LoadingDetails from 'Components/Lists/LoadingDetails';
import ExtraDetails from 'Components/Lists/ExtraDetails';
import Comment from 'Components/Lists/Comment';
import Locations from 'Components/Lists/Locations';
import Contact from 'Components/Lists/Contact';
import * as Data from 'Components/Data';
import { Loading } from 'Components/nui';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import Counterparties from 'Components/Lists/Counterparties';
import Documents from 'Components/Lists/Documents';
import DisplayedPrices from 'Pages/Products/DisplayedPrices';
import { unitsOfMeasure } from '~/utils';
import { FormattedMessage, useIntl } from 'react-intl';
import { IndexUnit, PriceIndex } from 'Components/nui/PriceIndex';
import { useLabels } from '~/utils';
import { getDeliveryFrequencyLabel } from '~/utils/deliveryFrequency';
import ActionList from 'Components/ActionList';
import { Link, useLocation } from 'react-router-dom';
import qs from 'qs';
import { useOrderPermissions } from '~/hooks';
import OrderActions from 'Components/OrderActions';
import { useOrder } from '~/hooks/useOrders';
import {
  useProductById,
  useProducts,
  useUserProductsTradeActions,
} from '~/hooks/useProducts';

interface MarketRowProps {
  id: string;
  warehouse?: { id: string };
  updated?: any;
}

export default ({ id, warehouse }: MarketRowProps) => {
  const { formatMessage } = useIntl();
  const label = useLabels();
  const solution = useStoreState((state) => state.auth.solution);
  const productsTradeActions = useUserProductsTradeActions();

  const { data: order, isLoading, refetch } = useOrder(id, warehouse);

  const [curCur, setCurCur] = useState<string | null>(null);
  const [pricesDrawerVis, setPricesDrawerVis] = useState(false);

  const productId = order?.product?.id;
  const baseProduct = useProductById(productId);
  const { data: allProducts = [] } = useProducts();

  // displayProduct: Used for row display (loading unit, value calculation, etc.)
  // Always prefer base product for display consistency
  const displayProduct = baseProduct;

  // Determine the required permission based on order type
  const requiredAction = order?.type === 'offer' ? 'buy' : 'sell';

  // tradeProduct + tradeActions: Used for trading (permissions, forms, buttons)
  // May fall back to variant product if user lacks base product permissions
  const { tradeProduct, tradeActions } = useMemo(() => {
    // If base product has the required permission, use it
    if (baseProduct?.id) {
      const baseTradeActions = productsTradeActions?.[baseProduct.id];
      if (baseTradeActions?.[requiredAction]) {
        return { tradeProduct: baseProduct, tradeActions: baseTradeActions };
      }
    }

    // Check variants for one with the required permission
    for (const variant of order?.variants || []) {
      const variantProduct = allProducts.find(
        (p) => p.id === variant.product?.id
      );
      const variantActions = productsTradeActions?.[variant.product?.id];
      if (variantProduct && variantActions?.[requiredAction]) {
        return { tradeProduct: variantProduct, tradeActions: variantActions };
      }
    }

    // Fallback: base product with any trade actions
    if (baseProduct?.id) {
      const baseTradeActions = productsTradeActions?.[baseProduct.id];
      if (baseTradeActions?.buy || baseTradeActions?.sell) {
        return { tradeProduct: baseProduct, tradeActions: baseTradeActions };
      }
    }

    // Fallback: any variant with any trade actions
    for (const variant of order?.variants || []) {
      const variantProduct = allProducts.find(
        (p) => p.id === variant.product?.id
      );
      const variantActions = productsTradeActions?.[variant.product?.id];
      if (variantProduct && (variantActions?.buy || variantActions?.sell)) {
        return { tradeProduct: variantProduct, tradeActions: variantActions };
      }
    }

    // Last fallback: base product or first accessible variant for trading
    if (baseProduct) {
      return { tradeProduct: baseProduct, tradeActions: {} };
    }
    const accessibleVariant = order?.variants?.find((v: any) =>
      allProducts.some((p) => p.id === v.product?.id)
    );
    const variantProduct = accessibleVariant
      ? (allProducts.find((p) => p.id === accessibleVariant.product?.id) ??
        null)
      : null;
    return { tradeProduct: variantProduct, tradeActions: {} };
  }, [
    baseProduct,
    order?.variants,
    allProducts,
    productsTradeActions,
    requiredAction,
  ]);

  const canViewCustomerPrices = solution.freight && 'sell' in tradeActions;

  const location = useLocation();
  const can = useOrderPermissions(order, tradeProduct);

  // Set currency when order loads or changes
  useEffect(() => {
    if (order?.price?.currency) {
      setCurCur(order.price.currency);
    }
  }, [order?.id, order?.price?.currency]);

  const attachments = order?.attachments;

  if (isLoading || !order) {
    return (
      <div className="mb-20 mt-10">
        <Loading size="medium" />
      </div>
    );
  }
  return (
    <div className="offers-bids-details" data-id={id}>
      <Dialog open={pricesDrawerVis} onOpenChange={setPricesDrawerVis}>
        <DialogContent className="tw:z-[9999]" overlayClassName="tw:z-[9999]">
          <DialogHeader>
            <DialogTitle>
              <FormattedMessage
                id="published-prices"
                defaultMessage="Published prices"
              />
            </DialogTitle>
          </DialogHeader>
          <DisplayedPrices orderId={order.pid} />
        </DialogContent>
      </Dialog>

      <div>
        <div className="table-box-three">
          <div className="unit-details">
            <h2 className="details">
              <FormattedMessage
                id="marketrow-orderdetails-header"
                description="Header for Order details in Marketrow"
                defaultMessage="Details"
              />
            </h2>

            {order.currencies && order.currencies.length > 1 && (
              <div className="unit-detail available-currencies">
                <h4>
                  <FormattedMessage
                    id="marketrow-orderdetails-field-availablecurrencies-label"
                    description="Label for `Available currencies` field on Order details in Marketrow"
                    defaultMessage="Available currencies"
                  />
                </h4>
                <ul>
                  {order.currencies.map((item) => (
                    <li key={item.currency}>
                      <button
                        className={classNames({
                          active: item.currency === curCur,
                        })}
                        type="button"
                        onClick={() => {
                          setCurCur(item.currency);
                        }}
                      >
                        {item.currency}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="unit-detail unit-detail-price">
              <h4>
                {label(
                  'price',
                  <FormattedMessage
                    id="marketrow-orderdetails-field-price-label"
                    description="Label for `Price` field on Order details in Marketrow"
                    defaultMessage="Price"
                  />
                )}
              </h4>
              {order._price.status === 'undetermined' ? (
                <span className="openordertypedetails">
                  <FormattedMessage
                    id="marketplace-table-subheading-price-not-available"
                    description="Label for `Not available` field on Order details in Marketrow"
                    defaultMessage="Not available"
                  />
                </span>
              ) : order._price.status === 'open' ? (
                <span
                  className={classNames('openordertypedetails', order.type)}
                >
                  <FormattedMessage
                    id="marketrow-orderdetails-field-open-label"
                    description="Label for `Open` field on Order details in Marketrow"
                    defaultMessage="Open"
                  />{' '}
                  {order.type}
                </span>
              ) : order.index ? (
                <>
                  <PriceIndex order={order} /> <IndexUnit order={order} />
                </>
              ) : order.price.val > 0 ? (
                <>
                  <FNumber
                    value={order.getPriceObjByCur(curCur).val}
                    decimalCount={order.priceDecimalCount}
                  />{' '}
                  <span>{order.getPriceObjByCur(curCur).unit}</span>
                  {order?._price?.[order._price.currency]?.tax !==
                    undefined && (
                    <>
                      <h4 className="mt-5">
                        <FormattedMessage
                          id="marketrow-orderdetails-field-tax-label"
                          description="Label for `Tax` field on Order details in Marketrow"
                          defaultMessage="Tax"
                        />{' '}
                      </h4>
                      {order._price[order._price.currency]?.tax ?? 'n/a'}{' '}
                      <span>
                        {order._price[order._price.currency]?.unit ?? 'n/a'}
                      </span>
                    </>
                  )}
                  {order?._price?.displayed_currency !== undefined && (
                    <>
                      <h4 className="mt-5">
                        <FormattedMessage
                          id="marketrow-orderdetails-field-ownerprice-label"
                          description="Label for `Owner price` field on Order details in Marketrow"
                          defaultMessage="Owner price"
                        />{' '}
                        ({order._price.displayed_incoterm ?? 'n/a'})
                      </h4>
                      <FNumber
                        value={
                          order._price?.[order._price.displayed_currency]?.[
                            order._price.displayed_incoterm
                          ]?.val
                        }
                        decimalCount={order.priceDecimalCount}
                      />{' '}
                      <span>
                        {order._price[order._price.displayed_currency]?.unit ??
                          'n/a'}
                      </span>
                    </>
                  )}
                  {canViewCustomerPrices && order.type !== 'bid' && (
                    <button
                      className="view-prices"
                      onClick={() => setPricesDrawerVis(true)}
                    >
                      <span>
                        <FormattedMessage
                          id="published-prices"
                          defaultMessage="Published prices"
                        />
                      </span>
                    </button>
                  )}
                </>
              ) : null}
            </div>

            <div className="unit-detail unit-detail-volume">
              <h4>
                {label(
                  'volume',
                  <FormattedMessage
                    id="marketrow-orderdetails-field-volume-label"
                    description="Label for `Volume` field on Order details in Marketrow"
                    defaultMessage="Volume"
                  />
                )}
              </h4>

              <div>
                <div className="block">
                  <FNumber
                    value={order.volume.available}
                    decimalCount={order.volumeDecimalCount}
                  />{' '}
                  <span>
                    {displayProduct
                      ? displayProduct.loadingunit?.desc
                      : (unitsOfMeasure[order.volume?.unit] ??
                        order.volume?.unit)}
                  </span>
                </div>
                {order.totaldeliveries && (
                  <>
                    <span className="qrts inline-block border mt-7 ml--5 nowrap">
                      {order.totaldeliveries} &times;{' '}
                      <FNumber
                        value={order.volume.delivery}
                        decimalCount={order.volumeDecimalCount}
                      />{' '}
                      {getDeliveryFrequencyLabel(
                        formatMessage,
                        order.deliveryfrequency
                      )}
                    </span>
                  </>
                )}
                {order.quarteretd && (
                  <>
                    <span className="qrts inline-block border mt-7 ml--5 nowrap">
                      <FNumber
                        value={order.volume.available / 3}
                        decimalCount={order.volumeDecimalCount}
                      />{' '}
                      <FormattedMessage
                        id="marketrow-field-etd-monthly"
                        description="'monthly' label for 'etd' field in Marketrow"
                        defaultMessage="monthly"
                      />
                    </span>
                  </>
                )}
              </div>
            </div>

            {order.price.val > 0 && !order.index && (
              <div className="unit-detail unit-detail-value">
                <h4>
                  <FormattedMessage
                    id="marketrow-orderdetails-field-value-label"
                    description="Label for `Value` field on Order details in Marketrow"
                    defaultMessage="Value"
                  />
                </h4>
                <FNumber
                  value={
                    order.value?.available ??
                    (displayProduct?.currency
                      ? displayProduct._getValue(
                          order.getPriceObjByCur(curCur).val,
                          order.volume.available,
                          order.loading.id
                        )
                      : 0)
                  }
                  decimalCount={2}
                />{' '}
                <span>
                  {order.value?.unit ?? order.getPriceObjByCur(curCur).currency}
                </span>
              </div>
            )}

            <div className="mt-10 mb-10">
              <OrderActions>
                <ActionList.Item>
                  <Link
                    to={`${location.pathname}?${qs.stringify({
                      order: order.pid,
                      tab: 'trade',
                    })}`}
                  >
                    <FormattedMessage
                      id="marketrow-tabs-header-trade"
                      description="Header for Trade on tabs in Marketrow"
                      defaultMessage="Trade"
                    />
                  </Link>
                </ActionList.Item>

                {can.decline && (
                  <ActionList.Item>
                    <Link
                      to={`${location.pathname}?${qs.stringify({
                        order: order.pid,
                        tab: 'decline',
                      })}`}
                    >
                      <FormattedMessage
                        id="marketrow-tabs-header-decline"
                        description="Header for Decline on tabs in Marketrow"
                        defaultMessage="Decline"
                      />
                    </Link>
                  </ActionList.Item>
                )}
                <ActionList.Item>
                  <Link
                    to={`${location.pathname}?${qs.stringify({
                      order: order.pid,
                      tab: 'counters',
                    })}`}
                  >
                    <FormattedMessage
                      id="marketrow-tabs-header-counters"
                      description="Header for Counters on tabs in Marketrow"
                      defaultMessage="Counters"
                    />
                  </Link>
                </ActionList.Item>
                {can.qa && (
                  <ActionList.Item>
                    <Link
                      to={`${location.pathname}?${qs.stringify({
                        order: order.pid,
                        tab: 'qa',
                      })}`}
                    >
                      <FormattedMessage
                        id="marketrow-tabs-header-qa"
                        description="Header for Q/A on tabs in Marketrow"
                        defaultMessage="Q&A"
                      />
                    </Link>
                  </ActionList.Item>
                )}
                {('buy' in tradeActions || 'sell' in tradeActions) &&
                  (order.is_owner || order.is_broker) && (
                    <OwnerButtons order={order} />
                  )}
                <PrepopulateButtons
                  order={order}
                  action
                  product={tradeProduct}
                  tradeActions={tradeActions}
                />
              </OrderActions>
            </div>
          </div>

          {tradeProduct && (
            <div className="trade-options">
              <Tabs
                order={order}
                product={tradeProduct}
                setCurCur={setCurCur}
                doRefresh={refetch}
              />
            </div>
          )}
        </div>

        <div className="table-box-three">
          <Comment record={order} />
          <div className="box">
            {order.attachments && order.attachments.length > 0 && (
              <Documents attachments={attachments} />
            )}
            {!!order.variants?.length && (
              <>
                <h2>
                  <FormattedMessage
                    id="marketrow-field-variants-label"
                    defaultMessage="Variants"
                  />
                </h2>
                <Data.ProductVariants
                  variants={order.variants.map(
                    ({ id, product: variantProduct, price, loading }) => {
                      // Build loading description with volume, container, and packaging
                      const unit = R.path(
                        ['loadingunit', 'desc'],
                        displayProduct
                      );
                      const loadingParts = [];

                      if (loading?.qty && unit) {
                        loadingParts.push(`${loading.qty} ${unit}`);
                      }
                      if (loading?.container) {
                        loadingParts.push(loading.container);
                      }
                      if (loading?.packaging) {
                        loadingParts.push(loading.packaging);
                      }

                      const loadingDesc =
                        loadingParts.length > 0
                          ? loadingParts.join(' | ')
                          : loading?.desc;

                      // Transform price using order's base price as template
                      const transformedPrice = price
                        ? new Price(order.price).new(price)
                        : undefined;

                      return {
                        id,
                        name: variantProduct.name,
                        price: transformedPrice,
                        loading: loadingDesc,
                      };
                    }
                  )}
                />
              </>
            )}
            <LoadingDetails
              loading={order.loading}
              unit={displayProduct?.loadingunit?.desc}
            />
            <Locations
              fromaddr={order.fromaddr}
              toaddr={order.toaddr}
              order={order}
            />
          </div>

          <div className="box">
            <ExtraDetails record={order} />

            <Counterparties order={order} />
            <Contact order={order} />

            {order.partners && (
              <>
                {'consignee' in order.partners && (
                  <Partner partners={order.partners} type="consignee" />
                )}
                {'buyer' in order.partners && (
                  <Partner partners={order.partners} type="buyer" />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
